"use client";

import { useEffect, useRef } from "react";

import { isDepartureOpen, type DepartureDto } from "@tripcart/shared";

import type { ApiFetchResult } from "@/lib/api-client";
import { parseLocalDate } from "@/lib/local-date";

import type { AvailabilityPayload } from "./page";

/**
 * `?date=YYYY-MM-DD` on a tour page: the departure a Departure Dates block's
 * Book now chose (docs/features/departure-dates AC-18). Read in the browser,
 * after load, so the tour page stays cacheable — its server render never
 * depends on the address.
 */
export const DATE_PARAM = "date";

/** The date the address asks for, when it is a departure open for booking. A
 *  sold-out, closed or unknown date chooses nothing. */
export function openDateFromAddress(
  search: string,
  departures: readonly DepartureDto[],
): string | null {
  const wanted = new URLSearchParams(search).get(DATE_PARAM);
  if (!wanted || !/^\d{4}-\d{2}-\d{2}$/.test(wanted)) return null;
  return departures.some((d) => d.date === wanted && isDepartureOpen(d))
    ? wanted
    : null;
}

/** The page's first departures: the server's promise on the dynamic tree,
 *  the hook's answer on the `sites` tree, `null` while that is pending. */
export type AddressDateSource =
  | Promise<ApiFetchResult<AvailabilityPayload>>
  | readonly DepartureDto[]
  | null;

/**
 * Chooses the address's date once, from the page's first departures — not the
 * booking box's, which on a phone mounts only when scrolled near. A date the
 * traveler picks afterwards is theirs. A date past that first window is not
 * chosen.
 */
export function useDateFromAddress(
  source: AddressDateSource,
  choose: (date: Date) => void,
): void {
  const done = useRef(false);
  useEffect(() => {
    if (done.current || source === null) return;
    let live = true;
    const pick = (departures: readonly DepartureDto[]) => {
      if (!live) return;
      done.current = true;
      const date = openDateFromAddress(window.location.search, departures);
      if (date) choose(parseLocalDate(date));
    };
    if (source instanceof Promise) {
      void source.then((result) => {
        if (result.ok) pick(result.data.departures);
      });
    } else {
      pick(source);
    }
    return () => {
      live = false;
    };
  }, [source, choose]);
}
